import { createSlice } from '@reduxjs/toolkit';
import { initialState } from './constants';
import {
  getCompanyMembers,
  getCompanyInfo,
  inviteMember,
  promoteMember,
  demoteMember,
  acceptInvitation,
  denyInvitation,
  getPaymentMethods,
  getDocumentDescriptions,
  getOrganizationPrintCredits,
  getPAF,
  editPaymentMethod,
  attachPaymentMethod,
} from './utils';
import { PaymentMethodsStatus } from 'src/utils/enums/payment-method-status.enum';

const companySlice = createSlice({
  name: 'stepper',
  initialState,
  reducers: {
    resetCompanyState: () => initialState,
    handleNewUserChange: (state, action) => {
      state.newUserForm.value = action.payload;
    },
    handleAutoExclusionChange: (state, action) => {
      state.autoExclusionForm.value = action.payload;
    },
    handleDescriptionNameChange: (state, action) => {
      state.descriptionForm.value = action.payload;
    },
    updateDescriptions: (state, action) => {
      state.documentDescriptions = action.payload;
    },
    updatePaymentMethods: (state, action) => {
      state.paymentMethods = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCompanyMembers.pending, (state) => {
        state.loadingTable = true;
      })
      .addCase(getCompanyMembers.fulfilled, (state, action) => {
        state.members = action.payload.items;
        state.loadingTable = false;
      })
      .addCase(getCompanyMembers.rejected, (state, action) => {
        state.fetchError = action.payload ? (action.payload as string) : 'Something went wrong!';
        state.loadingTable = false;
      })
      .addCase(getCompanyInfo.fulfilled, (state, action) => {
        state.info = action.payload.data;
      })
      .addCase(getCompanyInfo.rejected, (state, action) => {
        state.fetchError = action.payload ? (action.payload as string) : 'Something went wrong!';
        state.loadingTable = false;
      })
      .addCase(inviteMember.fulfilled, (state) => {
        state.newUserForm.errorMsg = '';
        state.newUserForm.successMsg = `Invitation sent to: ${state.newUserForm.value}`;
      })
      .addCase(inviteMember.rejected, (state, action) => {
        state.newUserForm.successMsg = '';
        state.newUserForm.errorMsg =
          action.payload && action.payload === 'Validation error at body'
            ? 'Wront email format!'
            : 'Something went wrong!';
      })
      .addCase(promoteMember.fulfilled, (state, action) => {
        state.members = state.members.map((item) =>
          item.id === action.payload.memberId ? { ...item, role: action.payload.role } : item,
        );
      })
      .addCase(demoteMember.fulfilled, (state, action) => {
        state.members = state.members.map((item) =>
          item.id === action.payload.memberId ? { ...item, role: action.payload.role } : item,
        );
      })
      .addCase(acceptInvitation.pending, (state) => {
        state.isProcessingInvitation = true;
      })
      .addCase(acceptInvitation.fulfilled, (state, action) => {
        state.isProcessingInvitation = false;
        state.members = state.members.map((item) =>
          item.id === Number(action.payload) ? { ...item, status: 'joined' } : item,
        );
      })
      .addCase(acceptInvitation.rejected, (state) => {
        state.isProcessingInvitation = false;
      })
      .addCase(denyInvitation.pending, (state) => {
        state.isProcessingInvitation = true;
      })
      .addCase(denyInvitation.fulfilled, (state, action) => {
        state.isProcessingInvitation = false;
        state.members = state.members.filter((item) => item.id !== Number(action.payload));
      })
      .addCase(denyInvitation.rejected, (state) => {
        state.isProcessingInvitation = false;
      })
      .addCase(getPaymentMethods.fulfilled, (state, action) => {
        state.paymentMethods = action.payload;
        if (action.payload.length === 0) {
          state.paymentMethodsStatus = PaymentMethodsStatus.MISSING;
        } else {
          state.paymentMethodsStatus = PaymentMethodsStatus.READY;
        }
      })
      .addCase(editPaymentMethod.pending, (state, action) => {
        state.editingPaymentMethod = true;
      })
      .addCase(editPaymentMethod.fulfilled, (state, action) => {
        state.editingPaymentMethod = false;
      })
      .addCase(editPaymentMethod.rejected, (state, action) => {
        state.editingPaymentMethod = false;
      })
      .addCase(getDocumentDescriptions.fulfilled, (state, action) => {
        state.documentDescriptions = action.payload.items;
      })
      .addCase(getOrganizationPrintCredits.fulfilled, (state, action) => {
        state.printCredits = action.payload.data;
      })
      .addCase(getPAF.fulfilled, (state, action) => {
        state.paf = action.payload.data;
      })
      .addCase(attachPaymentMethod.pending, (state) => {
        state.isAttachingPaymentMethod = true;
      })
      .addCase(attachPaymentMethod.fulfilled, (state) => {
        state.isAttachingPaymentMethod = false;
        state.paymentMethodsStatus = PaymentMethodsStatus.READY;
      })
      .addCase(attachPaymentMethod.rejected, (state) => {
        state.isAttachingPaymentMethod = false;
      });
  },
});

export const {
  resetCompanyState,
  handleNewUserChange,
  handleAutoExclusionChange,
  handleDescriptionNameChange,
  updateDescriptions,
  updatePaymentMethods,
} = companySlice.actions;

export default companySlice.reducer;
